import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart2,
  Building2,
  CreditCard,
  FileStack,
  FileText,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Receipt,
  Shield,
  Tag,
  Users,
  X,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { LegalComercioFooter } from "../components/legal/LegalComercioFooter";
import { Button } from "../components/ui/Button";
import { RegistrarActividadGlobalModal } from "../components/crm/RegistrarActividadGlobalModal";

const NAV_ITEMS = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/clientes", icon: Users, label: "Clientes" },
  { to: "/admin/crm", icon: FileStack, label: "Cartera CRM" },
  { to: "/admin/modalidades", icon: Tag, label: "Modalidades" },
  { to: "/admin/seguros", icon: Shield, label: "Seguros" },
  { to: "/admin/documentos", icon: FileText, label: "Documentos" },
  {
    to: "/admin/certificado-plantilla",
    icon: FileStack,
    label: "Plantilla certificado",
  },
  { to: "/admin/anuncios", icon: Megaphone, label: "Anuncios" },
  { to: "/admin/informes", icon: BarChart2, label: "Informes" },
  { to: "/admin/recibos", icon: Receipt, label: "Recibos" },
  {
    to: "/admin/cobros-mit",
    icon: CreditCard,
    label: "Cobros",
    badgeKey: "cobros" as const,
  },
  { to: "/admin/aseguradoras", icon: Building2, label: "Aseguradoras" },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cobrosPendientes, setCobrosPendientes] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const navItems = user?.role === "admin"
    ? NAV_ITEMS
    : NAV_ITEMS.filter((item) => item.to === "/admin/crm");

  useEffect(() => {
    if (user?.role !== "admin") {
      setCobrosPendientes(0);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get<{
          data: unknown[];
          incidencias?: unknown[];
        }>("/admin/cobros-mit");

        if (!cancelled) {
          setCobrosPendientes(
            (Array.isArray(res.data?.data) ? res.data.data.length : 0) +
              (Array.isArray(res.data?.incidencias)
                ? res.data.incidencias.length
                : 0)
          );
        }
      } catch {
        if (!cancelled) setCobrosPendientes(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen min-w-0 bg-[#F5F7FA]">
      {menuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-label="Cerrar menú"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-60 flex-col bg-[#1A3AB8] shadow-xl transition-transform duration-200 lg:translate-x-0 lg:shadow-none ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label="Navegación de administración"
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 bg-white px-4">
          <Logo className="h-8 max-w-[170px] origin-left scale-[1.75] object-contain lg:scale-[2]" />

          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 lg:hidden"
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3">
          {navItems.map(
            ({ to, icon: Icon, label, badgeKey }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/admin"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10 ${
                    isActive ? "bg-white/20" : ""
                  }`
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="min-w-0 flex-1">{label}</span>

                {badgeKey === "cobros" &&
                  cobrosPendientes > 0 && (
                    <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">
                      {cobrosPendientes}
                    </span>
                  )}
              </NavLink>
            )
          )}
        </nav>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-col lg:pl-[240px]">
        <header className="sticky top-0 z-10 flex min-h-16 items-center gap-2 border-b border-gray-200 bg-white px-3 sm:px-6 lg:justify-end lg:px-8">
          <div className="flex min-w-0 items-center gap-2 lg:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100"
              aria-label="Abrir menú"
              aria-expanded={menuOpen}
            >
              <Menu className="h-5 w-5" />
            </button>

            <Logo
              className="h-7 max-w-[120px] origin-left scale-150 object-contain"
              containerClassName="min-w-0"
            />
          </div>

          <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-4">
            <Button
              size="sm"
              onClick={() => setActivityModalOpen(true)}
              className="shrink-0"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden md:inline">Registrar actividad</span>
              <span className="md:hidden">Actividad</span>
            </Button>

            <span
              className="hidden max-w-[260px] truncate text-sm text-[#2C2C2C] sm:block"
              title={user?.email}
            >
              {user?.email}
            </span>

            <button
              type="button"
              onClick={handleLogout}
              className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm text-[#2C2C2C] hover:bg-gray-100"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Cerrar sesión</span>
            </button>
          </div>
        </header>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>

          <LegalComercioFooter />
        </main>
      </div>

      <RegistrarActividadGlobalModal
        isOpen={activityModalOpen}
        onClose={() => setActivityModalOpen(false)}
      />
    </div>
  );
}
